// apps/web/src/pages/onboarding/[token]/capacity.tsx — Phase 7 plan 07-06.
//
// Onboarding step 2: capacity baseline. Two fields
// (capacityDefault + holdbackDefault) mirror the Phase 3 D-13 spec.
// The server schema requires both fields as Int!; the client-side
// check enforces non-negative integers and a sensible max
// (10000 seats per day is generous).

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { WizardShell } from "../../../components/WizardShell";
import { gqlRequest } from "../../../lib/api";

const SAVE_CAPACITY_MUTATION = /* GraphQL */ `
  mutation SaveOnboardingCapacity($input: OnboardingCapacityInput!) {
    saveOnboardingCapacity(input: $input) {
      onboardingProgress
      restaurant {
        id
        slug
      }
    }
  }
`;

interface CapacityInput {
  capacityDefault: number;
  holdbackDefault: number;
}

type CapacityErrors = Partial<Record<keyof CapacityInput, boolean>>;

function validateCapacity(input: CapacityInput): CapacityErrors {
  const errs: CapacityErrors = {};
  if (
    !Number.isInteger(input.capacityDefault) ||
    input.capacityDefault < 1 ||
    input.capacityDefault > 10000
  ) {
    errs.capacityDefault = true;
  }
  if (
    !Number.isInteger(input.holdbackDefault) ||
    input.holdbackDefault < 0 ||
    input.holdbackDefault > 10000
  ) {
    errs.holdbackDefault = true;
  }
  return errs;
}

export function OnboardingCapacityPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState<CapacityInput>({
    capacityDefault: 0,
    holdbackDefault: 0,
  });
  const [errors, setErrors] = useState<CapacityErrors>({});

  const mutation = useMutation({
    mutationFn: () => gqlRequest(SAVE_CAPACITY_MUTATION, { input: form }),
    onSuccess: () => {
      navigate(`/onboarding/${token}/hours`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = validateCapacity(form);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  return (
    <WizardShell currentStep="capacity">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <h1 className="font-heading text-2xl">
          {t("onboarding.capacityTitle")}
        </h1>
        <p className="text-sm text-text/70">{t("onboarding.capacityHelp")}</p>
        <label className="flex flex-col gap-1">
          <span className="font-heading text-sm">
            {t("onboarding.capacityTotalSeats")}
          </span>
          <input
            type="number"
            min={1}
            max={10000}
            value={form.capacityDefault || ""}
            onChange={(e) =>
              setForm((s: CapacityInput) => ({
                ...s,
                capacityDefault:
                  e.target.value === "" ? 0 : Number(e.target.value),
              }))
            }
            className="field"
            data-testid="onboarding-capacity-input"
          />
          {errors.capacityDefault ? (
            <span className="text-[11px] text-red-700" role="alert">
              {t("onboarding.requiredField")}
            </span>
          ) : null}
        </label>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary"
          data-testid="onboarding-capacity-submit"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" aria-hidden />
              {t("onboarding.submitting")}
            </span>
          ) : (
            t("onboarding.saveNext")
          )}
        </button>
      </form>
    </WizardShell>
  );
}
